import { EditOutlined, LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MenuProps } from 'antd/lib'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useStocksServiceDeleteConsumableEndpoint,
  useStocksServiceGetConsumableListEndpoint,
  useStocksServiceGetConsumableListEndpointKey
} from 'src/api/queries'
import { ApiError, GetConsumableListResponseItem } from 'src/api/requests'
import {
  DeleteConfirm,
  message,
  SearchBar,
  TableActionButtons
} from 'src/components'
import { useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, resetPagination } from 'src/utils'

export const ConsumablesCard = () => {
  const { t } = useLocales()
  const navigate = useNavigate()
  const pagination = useTablePagination()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(!!search && {
      searchTerm: search
    })
  }

  const { data, isLoading } =
    useStocksServiceGetConsumableListEndpoint(queryParams)

  const { mutate } = useStocksServiceDeleteConsumableEndpoint({
    onError: (e: ApiError) => formErrorHandler(e.body.errors),
    onSuccess: () => {
      message.success(t('medicine_register.consumables.delete_success'))
      queryClient.invalidateQueries({
        queryKey: [useStocksServiceGetConsumableListEndpointKey]
      })
    }
  })

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const getActionItems = useMemo(
    () =>
      (id: string): MenuProps['items'] => {
        return [
          {
            key: 'edit',
            label: t('common.edit'),
            icon: <EditOutlined />,
            onClick: () =>
              navigate(paths.medicinesRegister.consumables.edit(id))
          },
          {
            key: 'divider',
            type: 'divider'
          },
          {
            key: 'delete',
            label: (
              <DeleteConfirm
                title={t('medicine_register.consumables.delete')}
                onConfirm={() => mutate({ id })}
              />
            ),
            danger: true
          }
        ]
      },
    [t]
  )

  const columns: ColumnsType<GetConsumableListResponseItem> = [
    {
      key: 'name',
      title: t('medicine_register.consumables.title'),
      dataIndex: 'commercialName'
    },
    {
      key: 'actions',
      width: 64,
      render: ({ id }) => (
        <TableActionButtons items={getActionItems(id)} justify="center" />
      )
    }
  ]

  return (
    <Card>
      <Flex vertical gap={16}>
        <Flex gap={16}>
          <Button
            icon={<LeftOutlined />}
            onClick={() => navigate(paths.medicinesRegister.root)}
          >
            {t('common.back')}
          </Button>

          <SearchBar onSearch={onSearch} className="flex-1" />

          <Button
            type="primary"
            onClick={() => navigate(paths.medicinesRegister.consumables.add)}
          >
            {t('common.add')}
          </Button>
        </Flex>

        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          bordered
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>
    </Card>
  )
}
